import React from 'react';
import styled from 'styled-components';
import Swal from 'sweetalert2';
import LeadService from '../../services/LeadService';
import { leadStatusShortLabel } from '../../constants/leadStatus';

const fetchLeadsDashboard = async () => {
  try {
    const response = await LeadService.getLeadsInfo();
    return response.data;
  } catch (error) {
    console.error('Error fetching leads:', error);
    throw error;
  }
};

const GroupsToolbar = styled.div`
  margin: 0 24px 16px 24px;
`;

const PrimaryDarkButton = styled.button`
  font-family: 'Inria Serif', serif;
  font-weight: 700;
  font-size: 0.9rem;
  color: #fff;
  background: rgb(208 125 63);
  border: none;
  border-radius: 10px;
  padding: 10px 20px;
  cursor: pointer;
  transition: background 0.15s ease, opacity 0.15s ease;

  &:hover {
    background: rgb(208 125 63);
  }
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const SecondaryOutlineButton = styled.button`
  font-family: 'Inria Serif', serif;
  font-weight: 600;
  font-size: 0.9rem;
  color: #1a1a1a;
  background: #fff;
  border: 1px solid #ced4da;
  border-radius: 10px;
  padding: 10px 20px;
  cursor: pointer;
  transition: background 0.15s ease;

  &:hover {
    background: #f8f9fa;
  }
`;

const GroupsEmptyPanel = styled.div`
  margin: 0 24px 24px 24px;
  min-height: 280px;
  border: 1px solid #dee2e6;
  border-radius: 12px;
  background: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 32px;
  text-align: center;
  color: #6c757d;
  font-size: 1rem;
  line-height: 1.5;
  font-family: 'Inria Serif', serif;
`;

const CreateGroupCard = styled.div`
  margin: 0 24px 24px 24px;
  background: #fff;
  border: 1px solid #dee2e6;
  border-radius: 12px;
  padding: 24px;
  width: calc(100% - 48px);
  font-family: 'Inria Serif', serif;
  box-sizing: border-box;
`;

const CreateGroupTitle = styled.h2`
  font-family: 'Inria Serif', serif;
  font-size: 1.15rem;
  font-weight: 700;
  color: #1a1a1a;
  margin: 0 0 20px 0;
`;

const FieldLabel = styled.label`
  display: block;
  font-family: 'Inria Serif', serif;
  font-weight: 700;
  font-size: 0.9rem;
  color: #1a1a1a;
  margin-bottom: 8px;
`;

const GroupNameInput = styled.input`
  width: 100%;
  padding: 12px 14px;
  border: 1px solid #dee2e6;
  border-radius: 10px;
  background: #f8f9fa;
  font-family: 'Inria Serif', serif;
  font-size: 0.95rem;
  margin-bottom: 22px;
  box-sizing: border-box;

  &::placeholder {
    color: #adb5bd;
  }
  &:focus {
    outline: none;
    border-color: #86b7fe;
    background: #fff;
  }
`;

const LeadsSelectScroll = styled.div`
  max-height: 320px;
  overflow-y: auto;
  border: 1px solid #e9ecef;
  border-radius: 10px;
  padding: 8px;
  margin-bottom: 22px;
  background: #fafbfc;
`;

const LeadPickRow = styled.label`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 14px;
  margin-bottom: 8px;
  background: #fff;
  border: 1px solid #e9ecef;
  border-radius: 10px;
  cursor: pointer;
  transition: border-color 0.15s ease;

  &:last-child {
    margin-bottom: 0;
  }
  &:hover {
    border-color: #ced4da;
  }
`;

const LeadPickMeta = styled.div`
  flex: 1;
  min-width: 0;
`;

const LeadPickName = styled.div`
  font-weight: 700;
  font-size: 0.95rem;
  color: #1a1a1a;
`;

const LeadPickCompany = styled.div`
  font-size: 0.8125rem;
  color: #6c757d;
  margin-top: 2px;
`;

const StatusPill = styled.span`
  flex-shrink: 0;
  font-size: 0.75rem;
  font-weight: 600;
  color: #495057;
  background: #e9ecef;
  padding: 4px 10px;
  border-radius: 999px;
`;

const FormActions = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
`;

const SavedGroupCard = styled.div`
  margin: 0 24px 12px 24px;
  width: calc(100% - 48px);
  padding: 16px 18px;
  background: #fff;
  border: 1px solid #e9ecef;
  border-radius: 10px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  font-family: 'Inria Serif', serif;
  box-sizing: border-box;
`;

const SavedGroupName = styled.div`
  font-weight: 700;
  font-size: 1rem;
  color: #1a1a1a;
`;

const SavedGroupMeta = styled.div`
  font-size: 0.85rem;
  color: #6c757d;
`;

const GroupCardActions = styled.div`
  display: flex;
  gap: 8px;
`;

const ActionButton = styled.button`
  border: 1px solid ${({ $variant }) => ($variant === 'danger' ? '#dc3545' : '#ced4da')};
  background: ${({ $variant }) => ($variant === 'danger' ? '#dc3545' : '#fff')};
  color: ${({ $variant }) => ($variant === 'danger' ? '#fff' : '#343a40')};
  padding: 6px 10px;
  border-radius: 8px;
  font-family: 'Inria Serif', serif;
  font-size: 0.82rem;
  cursor: pointer;

  &:hover {
    background: ${({ $variant }) => ($variant === 'danger' ? '#bb2d3b' : '#f8f9fa')};
  }
`;

const SearchBarRow = styled.div`
  margin: 0 24px 16px 24px;
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  align-items: center;
`;

const SearchInput = styled.input`
  min-width: 280px;
  flex: 1;
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #dee2e6;
  border-radius: 10px;
  background: #fff;
  font-family: 'Inria Serif', serif;
  font-size: 0.92rem;

  &:focus {
    outline: none;
    border-color: #86b7fe;
  }
`;

const PageSizeSelect = styled.select`
  padding: 10px 12px;
  border: 1px solid #dee2e6;
  border-radius: 10px;
  background: #fff;
  font-family: 'Inria Serif', serif;
  font-size: 0.9rem;
`;

const PaginationRow = styled.div`
  margin: 8px 24px 24px 24px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
  font-family: 'Inria Serif', serif;
`;

const PaginationMeta = styled.div`
  color: #6c757d;
  font-size: 0.86rem;
`;

const PaginationButtons = styled.div`
  display: flex;
  gap: 8px;
`;

const ErrorText = styled.div`
  margin: 0 24px 16px 24px;
  color: #dc3545;
  font-family: 'Inria Serif', serif;
  font-size: 0.9rem;
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 200px;
  font-family: 'Inria Serif', serif;
`;

const getLeadIdValue = (lead) =>
  lead?.leadId ?? null;

const getGroupIdValue = (group) =>
  group?.groupId ?? null;

const toIdString = (value) => (value == null ? '' : String(value));

const normalizeNameKey = (value) => (value || '').trim().toLowerCase();

const normalizeLeadGroupsPayload = (response) => {
  const data = response?.data?.data ?? {};
  const items = data?.items ?? [];
  const totalCount = Number(data?.totalCount ?? 0);
  const pageNumber = Number(data?.pageNumber ?? 1);
  const pageSize = Number(data?.pageSize ?? 10);

  return {
    items: Array.isArray(items) ? items : [],
    totalCount: Number.isFinite(totalCount) ? totalCount : 0,
    pageNumber: Number.isFinite(pageNumber) && pageNumber > 0 ? pageNumber : 1,
    pageSize: Number.isFinite(pageSize) && pageSize > 0 ? pageSize : 10,
  };
};

const SWEET_ALERT_CONFIRM_COLOR = 'rgb(208 125 63)';

const MIN_LEAD_GROUP_SEARCH_LENGTH = 3;

const normalizeLeadGroupSearchForApi = (value) => {
  const trimmed = String(value ?? '').trim();
  return trimmed.length >= MIN_LEAD_GROUP_SEARCH_LENGTH ? trimmed : '';
};

const LeadGroupsPage = () => {
  const [leads, setLeads] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [groupsLoading, setGroupsLoading] = React.useState(false);
  const [groupsUiMode, setGroupsUiMode] = React.useState('list');
  const [savedGroups, setSavedGroups] = React.useState([]);
  const [newGroupName, setNewGroupName] = React.useState('');
  const [selectedLeadIds, setSelectedLeadIds] = React.useState(() => new Set());
  const [searchText, setSearchText] = React.useState('');
  const [pageNumber, setPageNumber] = React.useState(1);
  const [pageSize, setPageSize] = React.useState(10);
  const [totalCount, setTotalCount] = React.useState(0);
  const [errorMessage, setErrorMessage] = React.useState('');
  const [editingGroupId, setEditingGroupId] = React.useState(null);
  const [selectedLeadNames, setSelectedLeadNames] = React.useState(() => new Set());
  const [contactIdsByLeadName, setContactIdsByLeadName] = React.useState({});

  const apiSearch = normalizeLeadGroupSearchForApi(searchText);

  const resetGroupForm = React.useCallback(() => {
    setNewGroupName('');
    setSelectedLeadIds(new Set());
    setSelectedLeadNames(new Set());
    setContactIdsByLeadName({});
    setEditingGroupId(null);
  }, []);

  React.useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        setLoading(true);
        const response = await fetchLeadsDashboard();
        const apiData = response.data || [];
        const transformed = apiData.map((lead) => ({
          name: lead?.name || '',
          leadId: lead?.leadId || '',
          company: lead?.company || '',
          leadsStatus: lead?.leadsStatus ?? lead?.LeadsStatus ?? '',
          email: lead?.email ?? lead?.Email ?? '',
          phoneNumber: lead?.phoneNumber ?? lead?.PhoneNumber ?? ''
        }));
        if (!cancelled) setLeads(transformed);
      } catch (e) {
        console.error(e);
        if (!cancelled) setLeads([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const loadLeadGroups = React.useCallback(async (query = {}) => {
    try {
      setGroupsLoading(true);
      setErrorMessage('');

      const requestedPage = query.pageNumber ?? pageNumber;
      const requestedPageSize = query.pageSize ?? pageSize;
      const searchForApi = normalizeLeadGroupSearchForApi(query.search);

      const response = await LeadService.getAllLeadGroups({
        PageNumber: requestedPage,
        PageSize: requestedPageSize,
        Search: searchForApi,
      });
      const normalized = normalizeLeadGroupsPayload(response);
      setSavedGroups(normalized.items);
      setTotalCount(normalized.totalCount);
      setPageNumber(normalized.pageNumber);
      setPageSize(normalized.pageSize);
    } catch (error) {
      console.error('Failed to fetch lead groups:', error);
      setSavedGroups([]);
      setTotalCount(0);
      setErrorMessage('Failed to fetch lead groups. Please try again.');
    } finally {
      setGroupsLoading(false);
    }
  }, [pageNumber, pageSize]);

  React.useEffect(() => {
    loadLeadGroups({ pageNumber, pageSize, search: apiSearch });
  }, [loadLeadGroups, pageNumber, pageSize, apiSearch]);

  const openCreateGroupForm = () => {
    resetGroupForm();
    setGroupsUiMode('create');
  };

  const handleCancelCreateGroup = () => {
    resetGroupForm();
    setGroupsUiMode('list');
  };

  const handleEditGroup = (group) => {
    const groupId = getGroupIdValue(group);
    const groupName = group?.name || group?.groupName || '';
    const contactIds = (group?.contacts || [])
      .map((c) => c?.leadId)
      .filter(Boolean)
      .map(toIdString);

    const fallbackLeadIds = (group?.leadIds || group?.leadIDs || [])
      .filter(Boolean)
      .map(toIdString);

    const mergedIds = [...new Set([...contactIds, ...fallbackLeadIds])];
    const nameToIds = {};
    const selectedNames = (group?.contacts || []).map((c) => {
      const normalizedName = normalizeNameKey(c?.name ?? c?.Name);
      const normalizedContactId = toIdString(
        c?.leadId ?? c?.LeadId ?? c?.leadID ?? c?.id ?? c?.Id,
      );

      if (normalizedName && normalizedContactId) {
        if (!nameToIds[normalizedName]) nameToIds[normalizedName] = new Set();
        nameToIds[normalizedName].add(normalizedContactId);
      }
      return normalizedName;
    }).filter(Boolean);

    setNewGroupName(groupName);
    setSelectedLeadIds(new Set(mergedIds));
    setSelectedLeadNames(new Set(selectedNames));
    setContactIdsByLeadName(
      Object.fromEntries(
        Object.entries(nameToIds).map(([name, ids]) => [name, Array.from(ids)]),
      ),
    );
    setEditingGroupId(groupId ? String(groupId) : null);
    setGroupsUiMode('create');
  };

  const handleDeleteGroup = async (group) => {
    const groupId = getGroupIdValue(group);
    if (!groupId) {
      await Swal.fire({
        icon: 'error',
        title: 'Delete Failed',
        text: 'Unable to delete this group because group id is missing.',
        confirmButtonColor: SWEET_ALERT_CONFIRM_COLOR,
      });
      return;
    }

    const groupName = group?.name || group?.groupName || 'this group';
    const confirmation = await Swal.fire({
      icon: 'warning',
      title: 'Delete Group?',
      text: `Delete "${groupName}"?`,
      showCancelButton: true,
      confirmButtonText: 'Delete',
      cancelButtonText: 'Cancel',
      confirmButtonColor: '#dc3545',
      cancelButtonColor: SWEET_ALERT_CONFIRM_COLOR,
    });
    if (!confirmation.isConfirmed) return;

    try {
      await LeadService.deleteLeadGroup(groupId);
      const nextPage = savedGroups.length === 1 && pageNumber > 1 ? pageNumber - 1 : pageNumber;
      setPageNumber(nextPage);
      await loadLeadGroups({ pageNumber: nextPage, pageSize, search: apiSearch });
      await Swal.fire({
        icon: 'success',
        title: 'Deleted',
        text: 'Group deleted successfully!',
        confirmButtonColor: SWEET_ALERT_CONFIRM_COLOR,
      });
    } catch (error) {
      console.error('Failed to delete group:', error);
      await Swal.fire({
        icon: 'error',
        title: 'Delete Failed',
        text: error?.response?.data?.message || 'Failed to delete lead group.',
        confirmButtonColor: SWEET_ALERT_CONFIRM_COLOR,
      });
    }
  };

  const setLeadSelectionForGroup = (lead, shouldSelect) => {
    const normalizedLeadId = toIdString(getLeadIdValue(lead));
    const normalizedLeadName = normalizeNameKey(lead?.name);

    if (!normalizedLeadId && !normalizedLeadName) return;

    setSelectedLeadIds((prev) => {
      const next = new Set(prev);
      if (normalizedLeadId) {
        if (shouldSelect) next.add(normalizedLeadId);
        else next.delete(normalizedLeadId);
      }
      const mappedIds = contactIdsByLeadName[normalizedLeadName] || [];
      mappedIds.forEach((id) => {
        if (shouldSelect) next.add(id);
        else next.delete(id);
      });
      return next;
    });

    setSelectedLeadNames((prev) => {
      const next = new Set(prev);
      if (!normalizedLeadName) return next;
      if (shouldSelect) next.add(normalizedLeadName);
      else next.delete(normalizedLeadName);
      return next;
    });
  };

  const handleSaveGroup = async () => {
    if (!newGroupName.trim()) {
      await Swal.fire({
        icon: 'warning',
        title: 'Validation',
        text: 'Please enter a group name.',
        confirmButtonColor: SWEET_ALERT_CONFIRM_COLOR,
      });
      return;
    }
    if (selectedLeadIds.size === 0) {
      await Swal.fire({
        icon: 'warning',
        title: 'Validation',
        text: 'Select at least one lead.',
        confirmButtonColor: SWEET_ALERT_CONFIRM_COLOR,
      });
      return;
    }

    const basePayload = {
      name: newGroupName.trim(),
      contacts: Array.from(selectedLeadIds).map((leadId) => ({
        leadId: String(leadId),
      })),
    };

    try {
      if (editingGroupId) {
        await LeadService.updateLeadGroup({
          ...basePayload,
          groupId: editingGroupId,
        });
        await Swal.fire({
          icon: 'success',
          title: 'Updated',
          text: 'Group updated successfully!',
          confirmButtonColor: SWEET_ALERT_CONFIRM_COLOR,
        });
      } else {
        await LeadService.createLeadGroup(basePayload);
        await Swal.fire({
          icon: 'success',
          title: 'Created',
          text: 'Group created successfully!',
          confirmButtonColor: SWEET_ALERT_CONFIRM_COLOR,
        });
      }
      resetGroupForm();
      setGroupsUiMode('list');
      setPageNumber(1);
      await loadLeadGroups({ pageNumber: 1, pageSize, search: apiSearch });
    } catch (error) {
      console.error('Failed to save group:', error);
      await Swal.fire({
        icon: 'error',
        title: 'Save Failed',
        text: error?.response?.data?.message || 'Failed to save lead group.',
        confirmButtonColor: SWEET_ALERT_CONFIRM_COLOR,
      });
    }
  };

  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const pageStart = totalCount === 0 ? 0 : (pageNumber - 1) * pageSize + 1;
  const pageEnd = Math.min(pageNumber * pageSize, totalCount);

  if (loading) {
    return (
      <LoadingContainer>
        <div>Loading…</div>
      </LoadingContainer>
    );
  }

  return (
    <>
      <GroupsToolbar>
        {groupsUiMode === 'list' && (
          <PrimaryDarkButton type="button" onClick={openCreateGroupForm}>
            Create Group
          </PrimaryDarkButton>
        )}
      </GroupsToolbar>

      {groupsUiMode === 'list' && (
        <SearchBarRow>
          <SearchInput
            type="text"
            value={searchText}
            placeholder="Search lead groups..."
            title="Enter at least 3 characters to search"
            onChange={(e) => {
              setPageNumber(1);
              setSearchText(e.target.value);
            }}
          />
          <PageSizeSelect
            value={pageSize}
            onChange={(e) => {
              setPageNumber(1);
              setPageSize(Number(e.target.value));
            }}
          >
            <option value={5}>5 / page</option>
            <option value={10}>10 / page</option>
            <option value={20}>20 / page</option>
          </PageSizeSelect>
        </SearchBarRow>
      )}

      {groupsUiMode === 'list' && errorMessage && <ErrorText>{errorMessage}</ErrorText>}

      {groupsUiMode === 'list' && groupsLoading && (
        <GroupsEmptyPanel>Loading groups...</GroupsEmptyPanel>
      )}

      {groupsUiMode === 'list' && !groupsLoading && savedGroups.length > 0 &&
        savedGroups.map((g) => (
          <SavedGroupCard key={g.groupId || g.id || g.name}>
            <div>
              <SavedGroupName>{g.name || g.groupName || 'Untitled Group'}</SavedGroupName>
              <SavedGroupMeta>
                {Number(g.contactCount ?? g.contacts?.length ?? 0)} lead
                {Number(g.contactCount ?? g.contacts?.length ?? 0) === 1 ? '' : 's'}
              </SavedGroupMeta>
            </div>
            <GroupCardActions>
              <ActionButton type="button" onClick={() => handleEditGroup(g)}>
                Edit
              </ActionButton>
              <ActionButton $variant="danger" type="button" onClick={() => handleDeleteGroup(g)}>
                Delete
              </ActionButton>
            </GroupCardActions>
          </SavedGroupCard>
        ))}

      {groupsUiMode === 'list' && !groupsLoading && savedGroups.length === 0 && (
        <GroupsEmptyPanel>
          No groups created yet. Click &apos;Create Group&apos; to get started.
        </GroupsEmptyPanel>
      )}

      {groupsUiMode === 'list' && totalCount > 0 && (
        <PaginationRow>
          <PaginationMeta>
            Showing {pageStart}-{pageEnd} of {totalCount}
          </PaginationMeta>
          <PaginationButtons>
            <SecondaryOutlineButton
              type="button"
              disabled={pageNumber <= 1 || groupsLoading}
              onClick={() => setPageNumber((prev) => Math.max(1, prev - 1))}
            >
              Previous
            </SecondaryOutlineButton>
            <SecondaryOutlineButton
              type="button"
              disabled={pageNumber >= totalPages || groupsLoading}
              onClick={() => setPageNumber((prev) => Math.min(totalPages, prev + 1))}
            >
              Next
            </SecondaryOutlineButton>
          </PaginationButtons>
        </PaginationRow>
      )}

      {groupsUiMode === 'create' && (
        <CreateGroupCard>
          <CreateGroupTitle>{editingGroupId ? 'Update Group' : 'Create New Group'}</CreateGroupTitle>
          <FieldLabel htmlFor="lead-group-name">Group Name</FieldLabel>
          <GroupNameInput
            id="lead-group-name"
            type="text"
            placeholder="Enter group name"
            value={newGroupName}
            onChange={(e) => setNewGroupName(e.target.value)}
            autoComplete="off"
          />
          <FieldLabel>Select Leads</FieldLabel>
          <LeadsSelectScroll>
            {leads.length === 0 ? (
              <div style={{ padding: '16px', color: '#6c757d', textAlign: 'center' }}>
                No leads available. Add leads from the Leads tab first.
              </div>
            ) : (
              leads.map((lead) => {
                const leadId = toIdString(getLeadIdValue(lead));
                const leadNameKey = normalizeNameKey(lead?.name);
                const isChecked =
                  selectedLeadIds.has(leadId) ||
                  (leadNameKey && selectedLeadNames.has(leadNameKey));

                return (
                  <LeadPickRow key={leadId || lead.name}>
                    <input
                      type="checkbox"
                      checked={Boolean(isChecked)}
                      onChange={(e) => setLeadSelectionForGroup(lead, e.target.checked)}
                    />
                    <LeadPickMeta>
                      <LeadPickName>{lead.name}</LeadPickName>
                      {lead.company ? <LeadPickCompany>{lead.company}</LeadPickCompany> : null}
                    </LeadPickMeta>
                    <StatusPill>{leadStatusShortLabel(lead.leadsStatus)}</StatusPill>
                  </LeadPickRow>
                );
              })
            )}
          </LeadsSelectScroll>
          <FormActions>
            <PrimaryDarkButton type="button" onClick={handleSaveGroup}>
              {editingGroupId ? 'Update Group' : 'Save Group'}
            </PrimaryDarkButton>
            <SecondaryOutlineButton type="button" onClick={handleCancelCreateGroup}>
              Cancel
            </SecondaryOutlineButton>
          </FormActions>
        </CreateGroupCard>
      )}
    </>
  );
};

export default LeadGroupsPage;
